import { useState, type FormEvent } from 'react';

const SUBMIT_ADDR = 'https://readdy.ai/api/form/dahbuvroh653ivfvnqv0';

type Status = 'idle' | 'submitting' | 'success' | 'error';

export default function ContactForm() {
  const [status, setStatus] = useState<Status>('idle');
  const [message, setMessage] = useState('');

  const handleSubmit = async (event: FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    const form = event.currentTarget;
    const fd = new FormData(form);

    const honeypot = String(fd.get('website_alt') ?? '').trim();
    if (honeypot) {
      setStatus('success');
      setMessage('已收到你的信息，我们会尽快查看并回复。');
      form.reset();
      return;
    }

    setStatus('submitting');
    setMessage('');

    const payload = new URLSearchParams();
    fd.forEach((value, key) => {
      if (key === 'website_alt') return;
      if (typeof value === 'string' && value.trim()) payload.append(key, value);
    });

    try {
      const response = await fetch(SUBMIT_ADDR, {
        method: 'POST',
        headers: { 'Content-Type': 'application/x-www-form-urlencoded' },
        body: payload.toString(),
      });

      const responseText = await response.text();
      let parsed: { code?: string; meta?: { message?: string; detail?: string }; message?: string } | null =
        null;
      try {
        parsed = JSON.parse(responseText);
      } catch {
        parsed = null;
      }

      const serverMsg =
        parsed?.meta?.message || parsed?.message || parsed?.meta?.detail || responseText || '';
      const isSpam = /spam/i.test(serverMsg) || /spam/i.test(parsed?.code ?? '');
      const ok = response.ok && parsed?.code === 'OK' && !isSpam;

      if (ok) {
        setStatus('success');
        setMessage('已收到你的信息，我们会尽快查看并回复。');
        form.reset();
      } else {
        setStatus('error');
        setMessage(serverMsg || '提交未成功，请稍后重试或直接发送邮件联系我们。');
      }
    } catch {
      setStatus('error');
      setMessage('网络连接异常，提交未成功，请稍后重试或直接发送邮件联系我们。');
    }
  };

  return (
    <form
      id="lead-form"
      data-readdy-form
      onSubmit={handleSubmit}
      className="rounded-lg border border-background-300 bg-background-50 p-6 md:p-8"
    >
      <h3 className="font-heading text-xl font-bold text-foreground-950">提交线索 / 联系我们</h3>
      <p className="mt-2 text-sm leading-relaxed text-foreground-600">
        简要描述你遇到的情况或想反馈的议题，我们会据此判断并尽快回复。
      </p>

      <div className="mt-6 grid grid-cols-1 gap-4 sm:grid-cols-2">
        <div className="flex flex-col gap-1.5">
          <label htmlFor="name" className="font-label text-sm text-foreground-800">
            称呼
          </label>
          <input
            id="name"
            name="name"
            type="text"
            required
            placeholder="如何称呼你"
            className="rounded-md border border-background-300 bg-background-50 px-3.5 py-2.5 text-sm text-foreground-900 outline-none transition-colors placeholder:text-foreground-400 focus:border-primary-400 focus:ring-1 focus:ring-primary-300"
          />
        </div>
        <div className="flex flex-col gap-1.5">
          <label htmlFor="email" className="font-label text-sm text-foreground-800">
            邮箱
          </label>
          <input
            id="email"
            name="email"
            type="email"
            required
            placeholder="用于接收回复"
            className="rounded-md border border-background-300 bg-background-50 px-3.5 py-2.5 text-sm text-foreground-900 outline-none transition-colors placeholder:text-foreground-400 focus:border-primary-400 focus:ring-1 focus:ring-primary-300"
          />
        </div>
      </div>

      <div className="mt-4 flex flex-col gap-1.5">
        <label htmlFor="topic_type" className="font-label text-sm text-foreground-800">
          议题类型
        </label>
        <select
          id="topic_type"
          name="topic_type"
          className="rounded-md border border-background-300 bg-background-50 px-3.5 py-2.5 text-sm text-foreground-900 outline-none transition-colors focus:border-primary-400 focus:ring-1 focus:ring-primary-300"
        >
          <option value="行业内幕">行业内幕</option>
          <option value="消费乱象">消费乱象</option>
          <option value="维权纠纷">维权纠纷</option>
          <option value="争议事件">争议事件</option>
          <option value="其他">其他</option>
        </select>
      </div>

      <div className="mt-4 flex flex-col gap-1.5">
        <label htmlFor="message" className="font-label text-sm text-foreground-800">
          情况描述
        </label>
        <textarea
          id="message"
          name="message"
          required
          rows={5}
          maxLength={500}
          placeholder="请简要说明时间、经过与已有材料（不超过 500 字）"
          className="resize-none rounded-md border border-background-300 bg-background-50 px-3.5 py-2.5 text-sm leading-relaxed text-foreground-900 outline-none transition-colors placeholder:text-foreground-400 focus:border-primary-400 focus:ring-1 focus:ring-primary-300"
        ></textarea>
      </div>

      <input
        type="text"
        name="website_alt"
        tabIndex={-1}
        autoComplete="off"
        aria-hidden="true"
        readOnly
        className="field-guard"
      />

      <div className="mt-6 flex flex-col gap-4 sm:flex-row sm:items-center sm:justify-between">
        <button
          type="submit"
          disabled={status === 'submitting'}
          className="inline-flex items-center justify-center gap-1.5 rounded-md bg-primary-600 px-6 py-3 font-label text-sm text-background-50 transition-colors hover:bg-primary-700 disabled:cursor-not-allowed disabled:opacity-60"
        >
          {status === 'submitting' ? '提交中…' : '提交信息'}
          {status !== 'submitting' && <i className="ri-send-plane-line" aria-hidden="true"></i>}
        </button>
        <p className="text-xs leading-relaxed text-foreground-500">
          提交即表示你同意我们仅为回复此事而使用你提供的信息。
        </p>
      </div>

      {status === 'success' && (
        <p
          role="status"
          className="mt-4 flex items-start gap-2 rounded-md border border-secondary-200 bg-secondary-50 px-4 py-3 text-sm text-secondary-900"
        >
          <i className="ri-checkbox-circle-line mt-0.5" aria-hidden="true"></i>
          {message}
        </p>
      )}

      {status === 'error' && (
        <p
          role="alert"
          className="mt-4 flex items-start gap-2 rounded-md border border-primary-200 bg-primary-50 px-4 py-3 text-sm text-primary-800"
        >
          <i className="ri-error-warning-line mt-0.5" aria-hidden="true"></i>
          {message}
        </p>
      )}
    </form>
  );
}